import React, { useEffect, useMemo, useState } from 'react'
import { InputText, Select } from '../../../ui'
import { useStore } from '../../../StateProvider'
import { equals, isEmpty, isNil, map } from 'ramda'
import { useTranslation } from 'react-i18next'
import { getUserValue } from '../utils'
import { NOT_ALLOWED_ADDRESS_SYMBOLS, STATE_CHECK_REGEX } from '../../../constants/constants'

const CANADIAN_STATES = [
  'Ontario',
  'Quebec',
  'Nova Scotia',
  'New Brunswick',
  'Manitoba',
  'Prince Edward Island',
  'Saskatchewan',
  'Alberta',
  'Newfoundland and Labrador',
  'Northwest Territories',
  'Yukon',
  'Nunavut'
]

const CustomStateSelect = {
  CAN: CANADIAN_STATES
}

const CustomCountries = Object.keys(CustomStateSelect)

/**
 * @param {{
 * i18nLabel: string,
 * label: string,
 * type: string,
 * name: string,
 * maxlength?: number,
 * additionalInfo?: string,
 * required: boolean,
 * order?: number,
 * allowNonLatin: boolean
 * }} props
 */
const ExternalState = ({
  i18nLabel,
  label,
  type,
  name,
  maxlength,
  additionalInfo,
  required,
  allowNonLatin
}) => {
  const { t } = useTranslation()
  const [value, setValue] = useState('')
  const [isValid, setIsValid] = useState(null)
  const [{ user, form, form: { accId } }, dispatch] = useStore()
  const { residenceCountry } = form[accId]
  const userValue = getUserValue(accId, user, name)
  const [showAsSelect, setShowAsSelect] = useState(null)
  const [loaded, setLoaded] = useState(false)
  const [isFilled, setIsFilled] = useState(false)
  const [error, setError] = useState('')
  const textLabel = i18nLabel ? t(`generatedField.${i18nLabel}`) : ''

  const options = useMemo(() => {
    const statesArray = CustomStateSelect[residenceCountry] ?? []
    return statesArray.map((state) => ({
      value: state,
      label: t(`generatedField.states.${state}`)
    }))
  }, [residenceCountry, t])

  /**
   * @param {string} value
   * @param {boolean} allowNonLatin
   * @returns {boolean}
   */
  const getIsValid = (value, allowNonLatin) => {
    return !NOT_ALLOWED_ADDRESS_SYMBOLS.test(value) && (allowNonLatin ? !!value.length : STATE_CHECK_REGEX.test(value))
  }

  /**
   * @param {string} value
   * @param {boolean} allowNonLatin
   * @returns {string}
   */
  const getErrorMsg = (value, allowNonLatin) => {
    return getIsValid(value, allowNonLatin)
      ? ''
      : allowNonLatin
        ? 'generatedField.notAllowedSpecialSymbols'
        : 'generatedField.onlyLatin'
  }

  /**
   * @param value {string}
   */
  const presetValue = (value) => {
    setValue(value)
    const isValid = getIsValid(value, allowNonLatin)
    setIsValid(isValid)
    const filled = !!value.trim()
    setIsFilled(filled)
    setError(!filled ? '' : getErrorMsg(value, allowNonLatin))
    dispatch({ type: 'setExtFieldValue', name, value })
    dispatch({ type: 'setValidationFld', name, value: isValid })
  }

  useEffect(() => {
    if (!loaded) {
      setLoaded((!isEmpty(user) && !isEmpty(form[accId])) || (isEmpty(user) && isEmpty(form[accId])))
    }
  }, [form[accId]])

  useEffect(() => {
    if (loaded) {
      const shouldShowAsSelect = CustomCountries.includes(residenceCountry)
      setShowAsSelect((current) => {
        if (current !== null && current !== shouldShowAsSelect) {
          setTimeout(() => presetValue(''))
        }
        return shouldShowAsSelect
      })
    }
  }, [residenceCountry, loaded])

  useEffect(() => {
    if (loaded && !isNil(userValue) && userValue !== '') {
      let correctValue = userValue
      if (options.length && !options.find(({ value }) => value === userValue)) {
        correctValue = ''
      }
      presetValue(correctValue)
    }
  }, [userValue, name, loaded])

  /**
   * @param {string} value
   */
  const getSelectValue = (value) => (equals(value, t('generatedField.choose')) ? '' : value)

  const onChangeSelect = ({ target: { value, name } }) => {
    const selectedValue = getSelectValue(value)
    setValue(selectedValue)
    const isValid = !required || selectedValue.length > 0
    setIsValid(isValid)
    dispatch({
      type: 'setExtFieldValue',
      name,
      value: selectedValue
    })

    dispatch({
      type: 'setValidationFld',
      name,
      value: selectedValue.length > 0
    })
  }

  /**
   * @param {React.ChangeEvent<HTMLInputElement>} e
   */
  const onChange = ({ target: { value } }) => {
    setValue(value)
    !value.trim() ? setIsFilled(false) : setIsFilled(true)
  }
  /**
   * @param {React.FocusEvent<HTMLInputElement>} e
   */
  const onBlur = ({ target: { name, value } }) => {
    const trimmedValue = value.trim()
    let isValid
    if (!required) {
      isValid = !trimmedValue.length || (trimmedValue.length && getIsValid(trimmedValue, allowNonLatin))
    } else {
      isValid = getIsValid(trimmedValue, allowNonLatin)
    }

    if (isValid) {
      setIsValid(true)
      setError('')
      dispatch({ type: 'setExtFieldValue', name, value })
    } else {
      setIsValid(false)
      setError(!isFilled ? '' : getErrorMsg(value, allowNonLatin))
    }

    dispatch({ type: 'setValidationFld', name, value: isValid })
  }

  if (showAsSelect) {
    return (
      <Select
        value={value || ''}
        label={t(label)}
        placeholder={t('generatedField.choose')}
        options={options}
        onChange={onChangeSelect}
        name={name}
        key={name}
        error=''
        valid={isValid}
        info={additionalInfo ? t(`generatedField.${name}AdditionalInfo`) : ''}
        showLabelAsPlaceholder={false}
      />
    )
  }

  return (
    <InputText
      name={name}
      label={textLabel}
      value={value}
      type={type}
      key={name}
      maxLength={maxlength || 200}
      error={t(error)}
      onChange={onChange}
      onBlur={onBlur}
      valid={isValid}
      info={additionalInfo ? t(`generatedField.${name}AdditionalInfo`) : ''}
    />
  )
}

const getExternalStateFields = map(currencyField => {
  const { i18n, name, required, type, order, maxLength, additionalInfo, allowNonLatin } = currencyField
  return (
    <ExternalState
      i18nLabel={i18n}
      label={i18n ? `generatedField.${i18n}` : ''}
      name={name}
      required={required}
      type={type}
      key={name}
      order={order}
      additionalInfo={additionalInfo}
      maxlength={maxLength}
      allowNonLatin={allowNonLatin}
    />
  )
})

export { ExternalState, getExternalStateFields }
